import storage from "./storage";
import jwtFilter from "../filters/jwt";
import ctx from "@/ctx";

const REQUEST_MODE = Object.freeze({ mode: "cors" });
const MAX_PATH_LEN = 2048;
const MAX_FILENAME_LEN = 255;

function normalizePath(path) {
  const parsed = String(path == null ? "" : path).trim();
  if (parsed.length < 1 || parsed.length > MAX_PATH_LEN) {
    throw new Error("Invalid API path");
  }
  if (/^[a-zA-Z][a-zA-Z\d+\-.]*:/.test(parsed)) {
    throw new Error("Absolute URL is not allowed in API path");
  }
  return parsed;
}

function parseId(value) {
  if (value == null || value === "") {
    throw new Error("Invalid id");
  }
  return String(value);
}

function safeFileName(filename, fallback = "file") {
  let value = fallback;
  if (filename != null && filename !== "") {
    const s = String(filename).trim();
    if (s.length >= 1 && s.length <= MAX_FILENAME_LEN) value = s;
  }
  return String(value).replace(/[\\/:*?"<>|\r\n]/g, "_");
}

/** API base: VUE_APP_BE в .env.development (локально) или тот же хост + /v1.0 на бою. */
function beBase() {
  const raw = process.env.VUE_APP_BE;
  if (raw != null && raw !== "") {
    const s = String(raw).trim();
    if (s !== "" && s !== "undefined") {
      return s.replace(/\/$/, "");
    }
  }
  if (process.env.NODE_ENV === "development") {
    return "";
  }
  if (typeof window !== "undefined" && window.location && window.location.origin) {
    return `${window.location.origin}/v1.0`;
  }
  return "";
}

const checkForError = response => {
  if (!response.ok) {
    if (response.status === 401) {
      storage.clear();
      return location.replace(location.origin + "/login");
    } else {
      response.json().then(res => {
        if (res.message) {
          ctx.system.msg = `[Error]: ${res.message}`;
        }
      });
    }
    throw Error(response.statusText);
  }
  return response;
};

const processingResponse = async response => {
  const type = response.headers.get("Content-Type");

  if (type && type.includes("sheet")) {
    const disposition = response.headers.get("Content-Disposition");

    let filename = "file.xlsx";
    if (disposition) {
      const match = disposition.match(/filename="?([^"]+)"?/);
      if (match) filename = match[1];
    }

    return {
      filename,
      blob: await response.blob()
    };
  }

  if (type?.includes("json")) return response.json();
  if (type?.includes("text")) return response.text();

  return true;
};

const processingCatch = error => {
  console.log("error", error);
};

async function Get(path) {
  const safePath = normalizePath(path);
  const jwt = await jwtFilter();
  const baseUrl = beBase();
  return await fetch(`${baseUrl}${safePath}`, {
    method: "GET",
    ...REQUEST_MODE,
    headers: {
      "Content-Type": "application/json; charset=utf-8",
      Authorization: "Bearer " + jwt
    }
  })
    .then(checkForError)
    .then(processingResponse)
    .catch(processingCatch);
}

async function Post(path, payload) {
  const safePath = normalizePath(path);
  const jwt = await jwtFilter();
  const baseUrl = beBase();
  return await fetch(`${baseUrl}${safePath}`, {
    method: "POST",
    ...REQUEST_MODE,
    credentials: "include",
    body:
      payload === null
        ? null
        : typeof payload === "object"
        ? JSON.stringify(payload)
        : payload,
    headers: {
      "Content-Type": "application/json; charset=utf-8",
      Authorization: "Bearer " + jwt
    }
  })
    .then(checkForError)
    .then(processingResponse)
    .catch(processingCatch);
}

async function Upload(path, file) {
  const jwt = await jwtFilter();
  const baseUrl = beBase();
  let formData = new FormData();
  formData.append("file", file);
  return await fetch(`${baseUrl}${path}`, {
    method: "POST",
    mode: "cors",
    body: formData,
    headers: {
      Authorization: "Bearer " + jwt
    }
  })
    .then(checkForError)
    .then(processingResponse)
    .catch(processingCatch);
}

async function UploadWithDirectory(path, file, directory) {
  const jwt = await jwtFilter();
  const baseUrl = beBase();
  let formData = new FormData();
  formData.append("file", file);
  if (directory) {
    formData.append("directory", directory);
  }
  return await fetch(`${baseUrl}${path}`, {
    method: "POST",
    mode: "cors",
    body: formData,
    headers: {
      Authorization: "Bearer " + jwt
    }
  })
    .then(checkForError)
    .then(processingResponse)
    .catch(processingCatch);
}

async function UploadCentrasContractFiles({ files, number, contractId, directory }) {
  const jwt = await jwtFilter();
  const baseUrl = beBase();
  const formData = new FormData();
  const list = Array.isArray(files) ? files : [files];
  for (const f of list) {
    if (f) formData.append("files", f);
  }
  if (number != null && String(number).trim() !== "") {
    formData.append("number", String(number).trim());
  }
  if (contractId != null && contractId !== "") {
    formData.append("contractId", String(contractId));
  }
  if (directory) {
    formData.append("directory", directory);
  }

  const response = await fetch(
    `${baseUrl}/api/insurance/contracts/centras-files/upload`,
    {
      method: "POST",
      mode: "cors",
      body: formData,
      headers: {
        Authorization: "Bearer " + jwt
      }
    }
  );

  if (!response.ok) {
    if (response.status === 401) {
      storage.clear();
      return location.replace(location.origin + "/login");
    }
    const msg = await readResponseErrorMessage(response);
    throw new Error(msg || response.statusText || "Ошибка загрузки");
  }

  return processingResponse(response);
}

/** RFC 5987: attachment; filename*=UTF-8''... и обычный filename="..." */
function parseFilenameFromContentDisposition(disposition) {
  if (!disposition || typeof disposition !== "string") return null;
  const star = disposition.match(
    /filename\*=(?:UTF-8|utf-8)''([^;\n]+)/i
  );
  if (star && star[1]) {
    try {
      return decodeURIComponent(star[1].trim().replace(/^["']|["']$/g, ""));
    } catch (e) {
      /* ignore */
    }
  }
  const quoted = disposition.match(/filename="([^"]+)"/i);
  if (quoted && quoted[1]) return quoted[1];
  const unquoted = disposition.match(/filename=([^;\s]+)/i);
  if (unquoted && unquoted[1]) {
    return unquoted[1].replace(/^["']|["']$/g, "");
  }
  return null;
}

async function readResponseErrorMessage(response) {
  try {
    const text = (await response.text()).trim();
    if (!text) return "";
    try {
      const json = JSON.parse(text);
      if (json && typeof json.message === "string" && json.message) {
        return json.message;
      }
      if (json && typeof json.error === "string" && json.error) {
        return json.error;
      }
      if (json && typeof json === "object") {
        const values = Object.values(json).filter(
          v => typeof v === "string" && v.trim() !== ""
        );
        if (values.length) return values.join("; ");
      }
    } catch (e) {
      /* not json */
    }
    return text;
  } catch (e) {
    return "";
  }
}

function saveBlobAsFile(blob, filename) {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = safeFileName(filename);
  link.style.display = "none";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  setTimeout(() => window.URL.revokeObjectURL(url), 1000);
}

async function downloadBinaryGet(path, fallbackName) {
  const safePath = normalizePath(path);
  const jwt = await jwtFilter();
  const baseUrl = beBase();
  const response = await fetch(`${baseUrl}${safePath}`, {
    method: "GET",
    ...REQUEST_MODE,
    headers: {
      Accept: "application/octet-stream, application/json, */*",
      Authorization: "Bearer " + jwt
    }
  });

  if (!response.ok) {
    if (response.status === 401) {
      storage.clear();
      location.replace(location.origin + "/login");
      throw new Error("Unauthorized");
    }
    const msg = await readResponseErrorMessage(response);
    throw new Error(msg || `Ошибка ${response.status}: ${response.statusText}`);
  }

  const disposition = response.headers.get("Content-Disposition");
  let filename =
    parseFilenameFromContentDisposition(disposition) ||
    safeFileName(fallbackName) ||
    "file";
  filename = safeFileName(filename);

  const blob = await response.blob();
  saveBlobAsFile(blob, filename);
  return true;
}

async function DownloadCentrasFile(fileIsn, fallbackName) {
  const q = encodeURIComponent(parseId(fileIsn));
  return downloadBinaryGet(
    `/api/insurance/contracts/centras-file?fileIsn=${q}`,
    fallbackName
  );
}

async function octellIntegrationApi(path, operatorID) {
  const baseUrl = beBase();
  const response = await fetch(`${baseUrl}${path}`, {
    method: "POST",
    headers: {
      // "Authorization": '9d9ceeef6957d77e15284f8f4c1ba7bf',
      "Content-Type": "application/json", // Добавлен заголовок Content-Type
    },
    body: JSON.stringify({
      operatorId: operatorID,
      secretKey: "6FvoQEN63SDOhosPKoTif0jwbYlrMnjOJJdK9J9BgwzdFk6X3jAS3fWdRrbdqXEPYZ11fMinPt5EJmUbDqowaCNjBFlbFbZKmIc4o8IlPXcqv1E4aT6jFyDBG26B5Ghl",
    }),
  });

  // Возвращаем результат асинхронного вызова
  return await response.json();
}

async function getMac() {
  try {
    const response = await fetch(`http://127.0.0.1:12345/mac`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json; charset=utf-8"
      }
    });

    if (!response.ok) {
      throw new Error("Ошибка при получении MAC: " + response.status);
    }

    const mac = await response.text();
    return mac;
  } catch (error) {
    console.error("Ошибка запроса к локальному агенту:", error);
    return null;
  }
}

export default {
  get: async path => {
    return await Get(path);
  },
  post: async (path, payload) => {
    return await Post(path, payload);
  },
  upload: async (path, file) => {
    return await Upload(path, file);
  },
  uploadWithDirectory: async (path, file, directory) => {
    return await UploadWithDirectory(path, file, directory);
  },
  downloadCentrasFile: async (fileIsn, fallbackName) => {
    return await DownloadCentrasFile(fileIsn, fallbackName);
  },
  uploadCentrasContractFiles: async options => {
    return await UploadCentrasContractFiles(options);
  },
  octell: async (path, operatorID) => {
    return await octellIntegrationApi(path, operatorID);
  },
  getMac: async ()=>{
    return await getMac();
  }
};
